import type { Agent, Partner } from "./types";

/**
 * Check if the agent has access to a specific resource
 */
export function hasResourceAccess(agent: Agent | null): boolean {
	if (!agent?.resources?.length) {
		return false;
	}
	const currentUrl = location.pathname.split("/")[2];
	if (!currentUrl) return true;
	return agent?.resources.includes(location.pathname.split("/")[2]);
}

/**
 * Check if partner has access to a specific nav item based on navbar_config.
 * An empty or missing navbar_config means no restrictions — allow everything.
 */
export function hasNavbarAccess(
	partner: Partner | null,
	requiredNavItem: string,
): boolean {
	const navbarConfig = partner?.partner_details?.navbar_config;
	// Empty or missing config = no restrictions, grant access to all
	if (!navbarConfig || navbarConfig.length === 0) {
		return true;
	}
	return navbarConfig.includes(requiredNavItem);
}

/**
 * Check if partner has access to any of the specified nav items.
 * An empty or missing navbar_config means no restrictions — allow everything.
 */
export function hasAnyNavbarAccess(
	partner: Partner | null,
	navItems: string[],
): boolean {
	const navbarConfig = partner?.partner_details?.navbar_config;
	// Empty or missing config = no restrictions, grant access to all
	if (!navbarConfig || navbarConfig.length === 0) {
		return true;
	}
	return navItems.some((item) => navbarConfig.includes(item));
}

/**
 * Check if the current user is a sub-partner (restricted access)
 * Sub-partner is determined by partner.type === 'sub_partner'
 */
export function isSubPartner(partner: Partner | null): boolean {
	return (
		partner?.partner?.type === "sub_partner" || partner?.is_sub_partner === true
	);
}

/**
 * Check if the current user is a partner admin
 * Role can be in set_profile_active[3].meta.role OR partner.role (for agent access)
 */
export function isPartnerAdmin(partner: Partner | null): boolean {
	const roleFromProfile = partner?.set_profile_active?.[3]?.meta?.role;
	const roleFromRoot = partner?.role;
	return roleFromProfile === "admin" || roleFromRoot === "admin";
}

/**
 * Get the partner's role
 */
export function getPartnerRole(partner: Partner | null): string | null {
	return partner?.set_profile_active?.[3]?.meta?.role || partner?.role || null;
}

/**
 * Check if partner has white label listings enabled
 */
export function hasWhiteLabelListings(partner: Partner | null): boolean {
	return partner?.partner_details?.white_label_listings ?? false;
}

/**
 * Check if settings page is visible for partner
 */
export function isSettingsPageVisible(partner: Partner | null): boolean {
	return partner?.partner_details?.is_settings_page_visible ?? false;
}

/**
 * Check if custom form is enabled for partner
 */
export function isCustomFormEnabled(partner: Partner | null): boolean {
	return partner?.partner_details?.is_partner_custom_form_enabled ?? false;
}

// Route authorization map
export type RouteKey =
	| "dashboard"
	| "leads"
	| "prospects"
	| "bookings"
	| "subpartners"
	| "listings"
	| "settings"
	| "customForm"
	| "customPage"
	| "whitelabelSettings"
	| "commissions"
	| "contracts"
	| "campaigns"
	| "integrations"
	| "teams"
	| "support"
	| "profile"
	| "select";

/**
 * Check if partner can access a specific route
 * Returns { allowed: boolean, reason?: string }
 */
export function canAccessRoute(
	partner: Partner | null,
	route: RouteKey,
): { allowed: boolean; reason?: string } {
	// Debug logging - remove in production

	if (!partner) {
		return { allowed: false, reason: "Not a partner" };
	}

	switch (route) {
		case "dashboard":
			return hasNavbarAccess(partner, "dashboard")
				? { allowed: true }
				: { allowed: false, reason: "No dashboard access" };

		case "leads":
			return hasNavbarAccess(partner, "leads")
				? { allowed: true }
				: { allowed: false, reason: "No leads access" };

		case "prospects":
			return hasNavbarAccess(partner, "prospects")
				? { allowed: true }
				: { allowed: false, reason: "No prospects access" };

		case "bookings": {
			// Bookings module is only for non-booking partners
			const isBookingPartner =
				(partner as any)?.partner_details?.is_booking_partner ||
				(partner as any)?.data?.is_booking_partner ||
				false;
			if (isBookingPartner) {
				return {
					allowed: false,
					reason: "Booking partners use Connect bookings",
				};
			}
			return hasNavbarAccess(partner, "leads")
				? { allowed: true }
				: { allowed: false, reason: "No bookings access" };
		}

		case "subpartners":
			if (isSubPartner(partner)) {
				return { allowed: false, reason: "Sub-partners cannot access this" };
			}
			return hasNavbarAccess(partner, "sub-partners")
				? { allowed: true }
				: { allowed: false, reason: "No sub-partners access" };

		case "listings":
			// Listings is accessible if either white_label_listings or regular listings config
			return hasNavbarAccess(partner, "white_label_listings") ||
				hasNavbarAccess(partner, "listings")
				? { allowed: true }
				: { allowed: false, reason: "No listings access" };

		case "settings":
			// if (!isSettingsPageVisible(partner)) {
			// 	return { allowed: false, reason: "Settings page not enabled" };
			// }
			return hasNavbarAccess(partner, "white_label_settings")
				? { allowed: true }
				: { allowed: false, reason: "No settings access" };

		case "customForm":
			// if (!isSettingsPageVisible(partner)) {
			// 	return { allowed: false, reason: "Settings page not enabled" };
			// }
			if (!isCustomFormEnabled(partner)) {
				return { allowed: false, reason: "Custom form not enabled" };
			}
			return hasNavbarAccess(partner, "white_label_settings")
				? { allowed: true }
				: { allowed: false, reason: "No settings access" };

		case "customPage":
			// if (!isSettingsPageVisible(partner)) {
			// 	return { allowed: false, reason: "Settings page not enabled" };
			// }
			return hasNavbarAccess(partner, "white_label_settings")
				? { allowed: true }
				: { allowed: false, reason: "No settings access" };

		case "whitelabelSettings":
			return hasNavbarAccess(partner, "white_label_settings")
				? { allowed: true }
				: { allowed: false, reason: "No whitelabel settings access" };

		case "commissions":
			if (isSubPartner(partner)) {
				return { allowed: false, reason: "Sub-partners cannot access this" };
			}
			if (!isPartnerAdmin(partner)) {
				return { allowed: false, reason: "Admin access required" };
			}
			return hasNavbarAccess(partner, "commissions")
				? { allowed: true }
				: { allowed: false, reason: "No commissions access" };

		case "contracts":
			// Static read-only page — always accessible
			return { allowed: true };

		case "campaigns":
			if (isSubPartner(partner)) {
				return { allowed: false, reason: "Sub-partners cannot access this" };
			}
			return hasNavbarAccess(partner, "campaigns")
				? { allowed: true }
				: { allowed: false, reason: "No campaigns access" };

		case "integrations":
			return hasNavbarAccess(partner, "integration")
				? { allowed: true }
				: { allowed: false, reason: "No integrations access" };

		case "support":
			return hasNavbarAccess(partner, "support")
				? { allowed: true }
				: { allowed: false, reason: "No support access" };

		// Always accessible routes
		case "teams":
		case "profile":
		case "select":
			return { allowed: true };

		default:
			return { allowed: false, reason: "Unknown route" };
	}
}
